// src/layouts/trafficmonitoring/routeFlows/functions.js
import i18n from "i18n";

/**
 * Keep only datasets that have a valid numeric array as data.
 */
export function filterValidDatasets(datasets) {
  return (datasets || []).filter((ds) => ds && Array.isArray(ds.data));
}

/**
 * Sanitize a list of chart configs:
 * - remove null/undefined
 * - ensure datasets are valid arrays
 */
export function sanitizeCharts(rawCharts) {
  return (rawCharts || []).filter(Boolean).map((chart) => ({
    ...chart,
    data: {
      ...(chart.data || {}),
      datasets: filterValidDatasets(chart.data?.datasets),
    },
  }));
}

/**
 * Compute the global max Y across all charts/datasets (safe).
 */
export function computeGlobalMaxY(charts) {
  let maxY = 0;
  (charts || []).forEach((chart) => {
    (chart.data?.datasets || []).forEach((ds) => {
      const localMax =
        Array.isArray(ds.data) && ds.data.length ? Math.max(...ds.data) : 0;
      if (Number.isFinite(localMax) && localMax > maxY) maxY = localMax;
    });
  });
  return maxY;
}

/**
 * Generate a harmonic set of HSL shades based on the values array.
 */
export function generateShades(
  values,
  { hue = 200, sat = 70, lightMin = 30, lightMax = 80 } = {}
) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return values.map((v) => {
    const norm = max === min ? 0.5 : (v - min) / (max - min);
    const lightness = lightMax - norm * (lightMax - lightMin);
    return `hsl(${hue}, ${sat}%, ${lightness}%)`;
  });
}

/**
 * Legend behavior compatible with Chart.js v3:
 * - toggle meta.hidden on click (like default)
 * - keep hidden state reflected in generated labels
 *
 * Requires passing ChartJS so we can access its defaults.
 */
export function makeSafeLegend(ChartJS) {
  return {
    onClick: (e, legendItem, legend) => {
      const chart = legend.chart;
      const idx = legendItem?.datasetIndex;
      if (idx == null) return;
      const meta = chart.getDatasetMeta(idx);
      if (!meta) return;
      meta.hidden =
        meta.hidden === null ? !chart.data.datasets[idx].hidden : null;
      chart.update();
    },
    labels: {
      // keep pointer cursor & proper hidden state reflection
      usePointStyle: false,
      generateLabels: (legendChart) => {
        const gen = ChartJS.defaults.plugins.legend.labels.generateLabels;
        const labels = typeof gen === "function" ? gen(legendChart) : [];
        return labels.map((l) => ({
          ...l,
          font: l.font || {},
          hidden: !legendChart.isDatasetVisible(l.datasetIndex),
        }));
      },
    },
  };
}

/**
 * Reasonable datalabels defaults (non-intrusive).
 * You can override in options.plugins.datalabels later if needed.
 */
export function makeDataLabelsDefaults({
  anchor = "end",
  align = "end",
  offset = -4,
  fontSize = 12,
  color = "#888a91",
  formatter = (v) => v ?? "",
} = {}) {
  return {
    anchor,
    align,
    offset,
    formatter,
    font: { size: fontSize },
    color,
  };
}

/**
 * Apply/override a yMax to an options object safely.
 */
export function applyGlobalYMax(
  options,
  yMax,
  { min = 0, beginAtZero = true } = {}
) {
  const base = options || {};
  const baseScales = base.scales || {};
  const baseY = baseScales.y || {};
  return {
    ...base,
    scales: {
      ...baseScales,
      y: { ...baseY, min, max: yMax, beginAtZero },
    },
  };
}

/**
 * Merge a chart's existing options with standard UX defaults:
 * - ensure legend is displayed with safe handlers
 * - set datalabels defaults
 * - (optionally) apply yMax
 */
export function enrichChartOptions({
  baseOptions,
  ChartJS,
  showLegend = true,
  datalabelsConfig = {},
  yMax = null,
}) {
  const legend = makeSafeLegend(ChartJS);
  const datalabels = makeDataLabelsDefaults(datalabelsConfig);

  let merged = {
    ...(baseOptions || {}),
    plugins: {
      ...(baseOptions?.plugins || {}),
      legend: {
        ...(baseOptions?.plugins?.legend || {}),
        display: showLegend,
        ...legend,
      },
      datalabels: {
        ...(baseOptions?.plugins?.datalabels || {}),
        ...datalabels,
      },
    },
  };

  if (yMax != null) {
    merged = applyGlobalYMax(merged, yMax, { min: 0, beginAtZero: true });
  }

  merged = {
    ...merged,
    scales: {
      ...(merged.scales || {}),
      y: {
        ...(merged.scales?.y || {}),
        title: {
          display: true,
          text: i18n.t("trafficMonitoring.routeFlows.axisFlow"), // Y-axis label
        },
      },
      x: {
        ...(merged.scales?.x || {}),
        title: {
          display: true,
          text: i18n.t("trafficMonitoring.routeFlows.axisCorridorIntersections"), // X-axis label
        },
      },
    },
  };

  return merged;
}
